import { useScrollReveal, useCountUp } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

interface MetricProps {
  label: string;
  value: number;
  suffix: string;
  sub: string;
  icon: string;
}

function MetricCard({ label, value, suffix, sub, icon }: MetricProps) {
  const { ref: countRef, value: displayValue } = useCountUp(
    value,
    2000,
    0,
    suffix === '%' || suffix === '秒' ? 1 : 0,
    true
  );

  return (
    <div className="group relative p-5 md:p-6 rounded-2xl glass-liquid border-primary-200/30 hover:border-primary-300/50 transition-all duration-500 hover:scale-[1.02] text-center">
      <div className="relative z-10">
        <div className="w-11 h-11 mx-auto mb-3 rounded-xl glass-blue border-primary-200/40 flex items-center justify-center">
          <i className={`${icon} text-lg text-primary-600`} />
        </div>
        <div ref={countRef} className="flex items-baseline justify-center gap-1 mb-2">
          <span className="number-font text-2xl md:text-3xl text-primary-700">{displayValue}</span>
          <span className="text-sm font-body text-foreground-950">{suffix}</span>
        </div>
        <p className="text-sm font-body font-semibold text-foreground-950 mb-1">{label}</p>
        <p className="text-xs text-foreground-950 font-body leading-relaxed">{sub}</p>
      </div>
    </div>
  );
}

const metrics = [
  { label: '提问频次', value: 12, suffix: '次/课', sub: '一节课提了几次问题', icon: 'ri-question-line' },
  { label: '平均等待时长', value: 3.2, suffix: '秒', sub: '每次问题后平均等待多久', icon: 'ri-hourglass-line' },
  { label: '追问次数', value: 5, suffix: '次', sub: '教师有没有对回答进行追问', icon: 'ri-chat-forward-line' },
  { label: '学生回应次数', value: 18, suffix: '次', sub: '学生主动或被动回应了多少次', icon: 'ri-user-voice-line' },
  { label: '回应进入教学比例', value: 67, suffix: '%', sub: '学生回应是否进入后续理论展开', icon: 'ri-pie-chart-line' },
];

export default function P16InteractionEvidence({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-16 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-100/8 blur-[150px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            从模糊到精确
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            "互动不足"<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">可以量化到哪些证据</span>
          </h2>
          <p className={`text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            如果只停留在评语里，老师很难知道要改哪里
          </p>
        </div>

        {/* Before → After illustration */}
        <div className={`max-w-md mx-auto mb-8 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center gap-4">
            <div className="flex-1 p-4 rounded-2xl glass border-white/50 text-center">
              <p className="text-xs text-foreground-950 font-body mb-1">传统评语</p>
              <p className="font-heading text-base font-bold text-foreground-950">互动不足</p>
            </div>
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-accent-100 border border-accent-200/40 flex items-center justify-center">
                <i className="ri-arrow-right-line text-accent-600" />
              </div>
            </div>
            <div className="flex-1 p-4 rounded-2xl glass-liquid border-primary-200/30 text-center">
              <p className="text-xs text-foreground-950 font-body mb-1">AI拆解</p>
              <p className="font-heading text-base font-bold text-primary-700">5项可量化证据</p>
            </div>
          </div>
        </div>

        {/* Five metric cards */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {metrics.map((metric, i) => (
            <div key={metric.label} style={{ transitionDelay: `${500 + i * 100}ms` }}>
              <MetricCard {...metric} />
            </div>
          ))}
        </div>

        <div className={`mt-8 text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl glass border-white/50 flex-wrap justify-center">
            <i className="ri-eye-line text-accent-500" />
            <span className="text-sm font-body text-foreground-950">
              管理者看到<strong className="text-primary-700">共性问题</strong>
              <span className="mx-2 text-foreground-950">|</span>
              教师看到<strong className="text-accent-700">具体改进点</strong>
              <span className="mx-2 text-foreground-950">|</span>
              专家看到<strong className="text-primary-700">评价依据</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}